import {
  faChevronLeft,
  faChevronRight,
  faFileCsv,
} from "@fortawesome/free-solid-svg-icons";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState, useCallback } from "react";
import { Card, CardBody, CardHeader, Container, Table } from "reactstrap";
import Button from "reactstrap/lib/Button";
import { reportsApi } from "../../../services/reportsServices";
import { payrollWeekApprovalApi } from "../../../services/payrollWeekApprovalServices";
import { otherTimeTypesApi } from "../../../services/otherTimeTypesServices";
import moment from "moment";
import { useAuth } from "../../../providers/authProvider";
import InformationModal from "../../../components/InformationModal";
import ConfirmationModal from "../../../components/ConfirmationModal";
import Loader from "../../../components/Loader";
import { useNavigate } from "react-router-dom";
import { onExportCSV } from "../../../utils/utils";
import TooltipItem from "../../../components/TooltipItem";
import { employeesApi } from "../../../services/employeeServices";
import {
  DateDetailsModal,
  NoteDisplay,
  PayrollTable,
} from "./payroll/components";
import {
  getDateLabel,
  isNonJobTimeType,
  LUNCH_HOURS,
  REVERSE_TIME_TYPE_MAPPING,
  TIME_TYPE_MAPPING,
} from "./payroll/helpers";

const PayrollReport = () => {
  const [authContext, setAuthContext] = useAuth();
  const navigate = useNavigate();
  const [tooltipOpen, setTooltipOpen] = useState(null);
  const [reloadPayrollWeekApproval, setReloadPayrollWeekApproval] =
    useState(false);

  const toggleTooltip = (index) => {
    setTooltipOpen((prevState) => (prevState === index ? null : index));
  };

  const [data, setData] = useState([]);
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState();
  const [otherTimeTypes, setOtherTimeTypes] = useState([]);
  const [payrollApprovalLoading, setPayrollApprovalLoading] = useState();
  const [payrollWeekApproved, setPayrollWeekApproved] = useState();
  const [monday, setMonday] = useState(moment().startOf("isoWeek"));

  const initConfirmationModal = {
    isOpen: false,
    onSubmit: null,
    onClose: null,
    title: "",
    body: "",
  };

  const [confirmationModal, setConfirmationModal] = useState(
    initConfirmationModal
  );

  const [informationModal, setInformationModal] = useState({
    isOpen: false,
    title: "",
    body: "",
  });

  const [notesModal, setNotesModal] = useState({
    isOpen: false,
    title: "",
    notes: [],
  });

  const previousMonday = () => setMonday(moment(monday).add(-1, "week"));

  const nextMonday = () => setMonday(moment(monday).add(1, "week"));

  const getSunday = () => moment(monday).endOf("isoWeek");

  const getWeekDates = useCallback(() => {
    const dates = [];
    let currentDate = moment(monday);
    while (dates.length < 7) {
      dates.push(currentDate);
      currentDate = moment(currentDate).add(1, "day");
    }
    return dates;
  }, [monday]);

  const getWeekDayValues = useCallback(
    (d) => {
      const weekDates = getWeekDates();
      const weekDayValues = {};
      weekDates.forEach((w) => {
        const key = getDateLabel(w);
        const dateData = d.payRoll.find(
          (e) => e.date === moment(w).format("YYYY-MM-DD")
        );
        let value = "-";
        if (dateData) {
          const reg = parseFloat(dateData.REG.toFixed(2));
          const ot = parseFloat(dateData.OT.toFixed(2));
          const double = parseFloat(dateData.DOUBLE.toFixed(2));
          const ptoReg = parseFloat(dateData.PTO.reg.toFixed(2));
          const ptoOt = parseFloat(dateData.PTO.ot.toFixed(2));
          const juryReg = parseFloat(dateData.JURY.reg.toFixed(2));
          const juryOt = parseFloat(dateData.JURY.ot.toFixed(2));
          const berReg = parseFloat(dateData.BER.reg.toFixed(2));
          const berOt = parseFloat(dateData.BER.ot.toFixed(2));
          const lunchReg = parseFloat(dateData.LUNCH.reg.toFixed(2));
          const lunchOt = parseFloat(dateData.LUNCH.ot.toFixed(2));
          const total = (
            reg +
            ot +
            double +
            ptoReg +
            ptoOt +
            juryReg +
            juryOt +
            berReg +
            berOt +
            lunchReg +
            lunchOt
          ).toFixed(2);
          value = total;
        }
        weekDayValues[key] = value;
      });
      return weekDayValues;
    },
    [getWeekDates]
  );

  const getResultDataFormatted = useCallback(
    (data) => {
      return data.map((d) => {
        const reg = parseFloat(d.weeklyPayroll.REG.toFixed(2));
        const ot = parseFloat(d.weeklyPayroll.OT.toFixed(2));
        const ptoReg = parseFloat(d.weeklyPayroll.PTO.reg.toFixed(2));
        const ptoOt = parseFloat(d.weeklyPayroll.PTO.ot.toFixed(2));
        const shopReg = parseFloat(d.weeklyPayroll.SHOP.reg.toFixed(2));
        const shopOt = parseFloat(d.weeklyPayroll.SHOP.ot.toFixed(2));
        const juryReg = parseFloat(d.weeklyPayroll.JURY.reg.toFixed(2));
        const juryOt = parseFloat(d.weeklyPayroll.JURY.ot.toFixed(2));
        const berReg = parseFloat(d.weeklyPayroll.BER.reg.toFixed(2));
        const berOt = parseFloat(d.weeklyPayroll.BER.ot.toFixed(2));
        const trainingReg = parseFloat(d.weeklyPayroll.TRAINING.reg.toFixed(2));
        const trainingOt = parseFloat(d.weeklyPayroll.TRAINING.ot.toFixed(2));
        const double = parseFloat(d.weeklyPayroll.DOUBLE.toFixed(2));
        const lunchReg = parseFloat(d.weeklyPayroll.LUNCH.reg.toFixed(2));
        const lunchOt = parseFloat(d.weeklyPayroll.LUNCH.ot.toFixed(2));
        const regApproval = parseFloat(d.weeklyPayroll.REG_APPROVAL.toFixed(2));
        const otApproval = parseFloat(d.weeklyPayroll.OT_APPROVAL.toFixed(2));
        const ptoApproval = parseFloat(d.weeklyPayroll.PTO_APPROVAL.toFixed(2));
        const trainingApproval = parseFloat(
          d.weeklyPayroll.TRAINING_APPROVAL.toFixed(2)
        );
        const shopApproval = parseFloat(
          d.weeklyPayroll.SHOP_APPROVAL.toFixed(2)
        );
        const juryApproval = parseFloat(
          d.weeklyPayroll.JURY_APPROVAL.toFixed(2)
        );
        const berApproval = parseFloat(d.weeklyPayroll.BER_APPROVAL.toFixed(2));
        const doubleApproval = parseFloat(
          d.weeklyPayroll.DOUBLE_APPROVAL.toFixed(2)
        );
        const lunchApproval = parseFloat(
          d.weeklyPayroll.LUNCH_APPROVAL.toFixed(2)
        );
        const total = (
          reg +
          ot +
          ptoReg +
          ptoOt +
          juryReg +
          juryOt +
          berReg +
          berOt +
          double +
          lunchReg +
          lunchOt
        ).toFixed(2);
        const totalApproved = (
          regApproval +
          otApproval +
          ptoApproval +
          shopApproval +
          juryApproval +
          berApproval +
          doubleApproval +
          lunchApproval +
          trainingApproval
        ).toFixed(2);

        return {
          employeeId: d.id,
          employee: d.firstName + " " + d.lastName,
          approvedBy: d.approvedBy,
          ...getWeekDayValues(d),
          regular: reg.toFixed(2),
          overtime: ot.toFixed(2),
          PTO: (ptoReg + ptoOt).toFixed(2),
          SHOP: (shopReg + shopOt).toFixed(2),
          JURY: (juryReg + juryOt).toFixed(2),
          BER: (berReg + berOt).toFixed(2),
          LUNCH: (lunchReg + lunchOt).toFixed(2),
          TRAINING: (trainingReg + trainingOt).toFixed(2),
          double: double.toFixed(2),
          total,
          totalApproved,
        };
      });
    },
    [getWeekDayValues]
  );

  useEffect(() => {
    const serviceLocation = authContext.serviceLocations.find(
      (sl) => sl.id === authContext.serviceLocationId
    );
    const payrollWeekApproved = serviceLocation?.payrollWeekApprovals?.find(
      (item) => item.weekStart === monday.format("YYYY-MM-DD")
    );
    setPayrollWeekApproved(payrollWeekApproved);
  }, [monday, authContext]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await otherTimeTypesApi.getOtherTimeTypes();
        setOtherTimeTypes(
          result.data.map(
            (otherTimeType) =>
              REVERSE_TIME_TYPE_MAPPING[otherTimeType.name] ||
              otherTimeType.name
          )
        );
      } catch (err) {
        return setInformationModal({
          isOpen: true,
          title: "Error",
          body: "There was an error getting the other time types.",
        });
      }
    };
    if (!otherTimeTypes.length) {
      fetchData();
    }
  }, [otherTimeTypes, setOtherTimeTypes]);

  useEffect(() => {
    const getPayrollReport = async () => {
      try {
        setLoading(true);
        const result = await reportsApi.payrollReport({
          weekStart: monday.format("YYYY-MM-DD"),
          serviceLocationId: authContext.serviceLocationId,
        });
        setLoading(false);
        const data = getResultDataFormatted(result);
        setRawData(result);
        setData(data);
      } catch (err) {
        setLoading(false);
        setInformationModal({
          isOpen: true,
          title: "Error",
          body:
            err?.response?.data[0].msg ||
            "There was an error with your request.",
        });
      }
    };
    getPayrollReport();

    if (reloadPayrollWeekApproval) {
      getPayrollReport();
      setReloadPayrollWeekApproval(false);
    }
  }, [
    monday,
    getResultDataFormatted,
    authContext.serviceLocationId,
    reloadPayrollWeekApproval,
  ]);

  const onSeeWODetails = (workOrderId) =>
    navigate(`/work-orders/${workOrderId}/details`);

  const onSeeCustomerDetails = (customerId) =>
    navigate(`/customers/${customerId}/details`);

  const onSeeOtherTimeNotes = (otherTimeType, notes) => {
    const filteredNotes = notes.filter(
      (note) => note.note && note.note.trim() !== ""
    );

    if (filteredNotes.length === 0) {
      setInformationModal({
        isOpen: true,
        title: "No Notes",
        body: "There are no notes for this time entry.",
      });
      return;
    }

    setNotesModal({
      isOpen: true,
      title: `Notes for ${TIME_TYPE_MAPPING[otherTimeType] || otherTimeType}`,
      notes: filteredNotes,
    });
  };

  const onSeeDateDetails = (entry, date) => {
    setInformationModal({
      isOpen: true,
      title: entry.employee,
      rawBody: true,
      size: "xl",
      body: (
        <DateDetailsModal
          entry={entry}
          date={date}
          rawData={rawData}
          otherTimeTypes={otherTimeTypes}
          onSeeWODetails={onSeeWODetails}
          onSeeCustomerDetails={onSeeCustomerDetails}
          onSeeOtherTimeNotes={onSeeOtherTimeNotes}
        />
      ),
    });
  };

  const onApprovePayrollWeek = async () => {
    if (!authContext.serviceLocationId) {
      setInformationModal({
        isOpen: true,
        title: "Payroll Approval",
        body: "Please select a specific location first.",
      });
    } else {
      setConfirmationModal({
        isOpen: true,
        onSubmit: async () => {
          try {
            setConfirmationModal(initConfirmationModal);
            setPayrollApprovalLoading(true);
            await payrollWeekApprovalApi.approvePayrollWeek({
              weekStart: monday.format("YYYY-MM-DD"),
              serviceLocationId: authContext.serviceLocationId,
            });
            setPayrollApprovalLoading(false);
            setInformationModal({
              isOpen: true,
              title: "Payroll Approved",
              body: "Payroll week approved successfully.",
            });
            setAuthContext({
              ...authContext,
              serviceLocations: [],
            });
            setReloadPayrollWeekApproval(true);
          } catch (err) {
            setConfirmationModal(initConfirmationModal);
            setPayrollApprovalLoading(false);
            setInformationModal({
              isOpen: true,
              title: "Error",
              body:
                err?.response?.data[0].msg ||
                "There was an error with your request.",
            });
          }
        },
        onClose: () => {
          setConfirmationModal(initConfirmationModal);
        },
        title: "Payroll Approval",
        body: `<span class="text-center">Do you confirm you want to approve this payroll week?</span>`,
        confirmColor: "danger",
      });
    }
  };

  const onUndoPayrollWeekApproval = async () => {
    setConfirmationModal({
      isOpen: true,
      onSubmit: async () => {
        try {
          setConfirmationModal(initConfirmationModal);
          setPayrollApprovalLoading(true);
          await payrollWeekApprovalApi.undoPayrollWeekApproval({
            id: payrollWeekApproved.id,
          });
          setPayrollApprovalLoading(false);
          setInformationModal({
            isOpen: true,
            title: "Undo Payroll Approval",
            body: "Payroll week approval was successfully undone.",
          });
          setAuthContext({
            ...authContext,
            serviceLocations: [],
          });
          setReloadPayrollWeekApproval(true);
        } catch (err) {
          setConfirmationModal(initConfirmationModal);
          setPayrollApprovalLoading(false);
          setInformationModal({
            isOpen: true,
            title: "Error",
            body:
              err?.response?.data[0].msg ||
              "There was an error with your request.",
          });
        }
      },
      onClose: () => {
        setConfirmationModal(initConfirmationModal);
      },
      title: "Undo Payroll Approval",
      body: `<span class="text-center">Do you confirm you want to undo the approval of this payroll week?</span>`,
      confirmColor: "danger",
    });
  };

  const onSeeOtherDetails = (entry, isCaliforniaPayroll = false) => {
    setInformationModal({
      isOpen: true,
      title: `Other Time Details`,
      rawBody: true,
      size: "xl",
      body: (
        <Table className="border rounded mb-0">
          <thead className="small text-muted">
            <tr>
              <th>Employee</th>
              {otherTimeTypes
                .filter((type) => {
                  if (
                    !isCaliforniaPayroll &&
                    type === LUNCH_HOURS &&
                    entry.LUNCH === "0.00"
                  ) {
                    return false;
                  }
                  return isNonJobTimeType(type);
                })
                .map((otherTimeType, index) => (
                  <th key={index}>
                    {TIME_TYPE_MAPPING[otherTimeType] || otherTimeType}
                  </th>
                ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{entry.employee}</td>
              {otherTimeTypes
                .filter((type) => {
                  if (
                    !isCaliforniaPayroll &&
                    type === LUNCH_HOURS &&
                    entry.LUNCH === "0.00"
                  ) {
                    return false;
                  }
                  return isNonJobTimeType(type);
                })
                .map((otherTimeType, index) => (
                  <td key={index}>{entry[otherTimeType] || "0.00"}</td>
                ))}
            </tr>
          </tbody>
        </Table>
      ),
    });
  };

  const handleExportCSV = () => {
    onExportCSV(rawData, monday);
  };

  const shouldShowDoubleTime =
    rawData.length &&
    rawData.some((entry) =>
      entry.payRoll.some((payRollEntry) => payRollEntry.IS_CALIFORNIA_PAYROLL)
    );

  useEffect(() => {
    const fetchAuthContext = async () => {
      try {
        const result = await employeesApi.getEmployees({
          id: authContext.currentUser.id,
        });
        if (result.serviceLocations?.length === 1) {
          setAuthContext({
            ...authContext,
            serviceLocationId: result.serviceLocations[0].id,
          });
        }
      } catch (err) {
        setInformationModal({
          isOpen: true,
          title: "Error",
          body: "There was an error fetching the authentication context.",
        });
      }
    };

    if (!authContext.serviceLocationId) {
      fetchAuthContext();
    }
  }, [authContext, setAuthContext]);

  return (
    <Container fluid>
      <Card className="col-12 px-0">
        <CardHeader className="d-flex align-items-center justify-content-between mt-2">
          <div className="text-dark flex-grow-1 d-flex align-items-center">
            <h3 className="mb-0 ">Payroll Report</h3>
            <small className="text-muted ml-2 pt-1">({data.length})</small>
          </div>
          {payrollApprovalLoading ? (
            <div className="min-width-50">
              <Loader size="sm" />
            </div>
          ) : !payrollWeekApproved ? (
            <>
              {data?.length ? (
                !authContext.serviceLocationId ? (
                  <TooltipItem
                    id={monday}
                    title={"You must select a service location first"}
                  >
                    <Button
                      color={"success"}
                      onClick={onApprovePayrollWeek}
                      disabled={true}
                      className="mr-2 rounded"
                      size="sm"
                    >
                      Approve for Payroll
                    </Button>
                  </TooltipItem>
                ) : (
                  <Button
                    color={"success"}
                    onClick={onApprovePayrollWeek}
                    className="mr-2 rounded"
                    size="sm"
                  >
                    Approve for Payroll
                  </Button>
                )
              ) : null}
            </>
          ) : (
            <div className="d-flex justify-content-center align-items-center">
              <span className="text-muted small mr-2">
                {`Approved at `}
                {moment(payrollWeekApproved.createdAt).format("MM-DD-YYYY")}
              </span>
              <Button
                color={"danger"}
                onClick={onUndoPayrollWeekApproval}
                className="mr-2 rounded"
                size="sm"
              >
                Undo Approval
              </Button>
            </div>
          )}
          <div className="d-flex justify-content-between">
            <Button className="my-1" onClick={previousMonday}>
              <FontAwesomeIcon icon={faChevronLeft} />
            </Button>
            <strong className="my-1 mx-1 p-2 border rounded">
              {monday.format("MM/DD/YYYY")}
              {" - "}
              {getSunday().format("MM/DD/YYYY")}
            </strong>
            <Button
              className="my-1"
              onClick={nextMonday}
              disabled={getSunday().isAfter(moment())}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </Button>
          </div>
          <Button
            color={"primary"}
            onClick={handleExportCSV}
            disabled={!data?.length}
            className="ml-2 rounded"
            size="sm"
          >
            <FontAwesomeIcon icon={faFileCsv} className="mr-2" />
            Download
          </Button>
        </CardHeader>
        <CardBody className="d-flex flex-column">
          {loading ? (
            <Loader size="sm" />
          ) : (
            <PayrollTable
              data={data}
              rawData={rawData}
              monday={monday}
              otherTimeTypes={otherTimeTypes}
              shouldShowDoubleTime={shouldShowDoubleTime}
              tooltipOpen={tooltipOpen}
              onToggleTooltip={toggleTooltip}
              onSeeDateDetails={onSeeDateDetails}
              onSeeOtherDetails={onSeeOtherDetails}
            />
          )}
        </CardBody>
      </Card>
      {confirmationModal.isOpen ? (
        <ConfirmationModal {...confirmationModal} />
      ) : informationModal.isOpen ? (
        <InformationModal
          title={informationModal.title}
          body={informationModal.body}
          rawBody={informationModal.rawBody}
          size={informationModal.size}
          onClose={() =>
            setInformationModal({ isOpen: false, title: "", body: "" })
          }
        />
      ) : null}

      {notesModal.isOpen && (
        <InformationModal
          title={notesModal.title}
          rawBody={true}
          size="md"
          body={
            <div>
              {notesModal.notes.length === 1 ? (
                <NoteDisplay
                  note={notesModal.notes[0]}
                  showEntryNumber={false}
                />
              ) : (
                <div>
                  {notesModal.notes.map((note, index) => (
                    <NoteDisplay
                      key={index}
                      note={note}
                      entryNumber={index + 1}
                      showEntryNumber={true}
                      className="mb-3"
                    />
                  ))}
                </div>
              )}
            </div>
          }
          onClose={() => {
            setNotesModal({ isOpen: false, title: "", notes: [] });
          }}
        />
      )}
    </Container>
  );
};

export default PayrollReport;
